import React, { useState } from "react";
import {
  Box,
  Flex,
  Avatar,
  Input,
  IconButton,
  Portal,
  Divider,
  Text,
  Badge,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
} from "@chakra-ui/react";
import {
  ArrowUpIcon,
  ChatBubbleOvalLeftIcon,
  XMarkIcon,
  EllipsisHorizontalIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";
import { BASE_API_URL } from "./config";
import { PALETTES, useAppColorMode } from "./appColors";

// Comment UI renders through portals (outside the .stande-app wrapper), so CSS
// var tokens don't reach it — resolve a literal palette from the saved mode.
const usePalette = () => {
  const { isDark } = useAppColorMode();
  return isDark ? PALETTES.dark : PALETTES.light;
};

// Author avatars stay brand-blue in both modes.
const AVATAR_BG = "#9AE9FE";
const AVATAR_FG = "#000000";

export const createCommentThread = async (
  { x, y, user_paper_attempt_guid, question_guid, user_guid },
  token
) => {
  const { data } = await axios.post(
    `${BASE_API_URL}/api/v1/user-question-attempt-comment-threads/`,
    {
      user_paper_attempt_guid,
      question_guid,
      x_pos: x,
      y_pos: y,
      user_guid,
    },
    {
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
  );
  return data;
};

export const createComment = async (
  { comment_thread_guid, user_guid, comment_text },
  token
) => {
  const { data } = await axios.post(
    `${BASE_API_URL}/api/v1/user-question-attempt-comments/`,
    { comment_thread_guid, user_guid, comment_text },
    {
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
  );
  return data;
};

// FastAPI returns `detail` as a plain string for most errors, but as an array
// of Pydantic validation-error objects ({type, loc, msg, input}) for 422s.
// Chakra's toast renders `description` directly as a child, so an unhandled
// array/object there crashes React - always reduce it to a plain string.
export const extractErrorMessage = (err) => {
  const detail = err?.response?.data?.detail;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    const message = detail
      .map((d) => {
        if (typeof d === "string") return d;
        const field = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== "body").join(".") : "";
        return field ? `${field}: ${d?.msg}` : d?.msg;
      })
      .filter(Boolean)
      .join("; ");
    return message || "Please try again.";
  }

  return err?.message || "Please try again.";
};

// Normalizes whatever the API returns for a question's comment threads into
// the shape the UI works with. Defaults to [] so the feature degrades
// gracefully if the backend doesn't (yet) return this field.
export const normalizeQuestionThreads = (question, { currentUserGuid, currentUserInitial } = {}) => {
  const apiThreads = Array.isArray(question?.user_question_attempt_comment_threads)
    ? question.user_question_attempt_comment_threads
    : [];

  return apiThreads.map((t, idx) => {
    const comments = Array.isArray(t.comments) ? t.comments : [];
    const xPct = Math.max(0, Math.min(100, Number(t.x_pct ?? t.x_pos) || 0));
    const yPct = Math.max(0, Math.min(100, Number(t.y_pct ?? t.y_pos) || 0));

    // The thread wrapper itself doesn't reliably carry its own guid - each
    // comment does (that's the same field the create-comment call sends as
    // `comment_thread_guid`), so fall back to that before the synthetic id.
    const threadGuid =
      t.guid ||
      t.comment_thread_guid ||
      comments.find((c) => c.comment_thread_guid)?.comment_thread_guid ||
      `${question.guid}-thread-${idx}`;

    return {
      id: threadGuid,
      questionGuid: question.guid,
      xPct,
      yPct,
      comments: comments.map((c, i) => {
        // Only the viewer's own comments should ever be labeled with the
        // viewer's own name/initial - never use it as a generic fallback for
        // an unnamed comment, or every other commenter's avatar silently
        // becomes "you".
        const isSelf = Boolean(c.user_guid) && c.user_guid === currentUserGuid;
        return {
          id: c.guid || `${threadGuid}-${i}`,
          text: c.comment_text || "",
          userGuid: c.user_guid || "",
          userName: c.user_name || (isSelf ? "You" : "User"),
          userInitial: (c.user_name && c.user_name[0]?.toUpperCase()) || (isSelf ? currentUserInitial : "?"),
          createdAt: c.created_at || new Date().toISOString(),
        };
      }),
    };
  });
};

export const formatRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));

  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;

  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const UserAvatar = (props) => (
  <Avatar bg={AVATAR_BG} color={AVATAR_FG} fontWeight="700" {...props} />
);

const RoundArrowButton = ({ ariaLabel, ...props }) => {
  const P = usePalette();
  return (
    <IconButton
      aria-label={ariaLabel}
      icon={<ArrowUpIcon width={14} height={14} />}
      isRound
      size="xs"
      variant="outline"
      borderColor={P.border}
      bg={P.surface}
      color={P.text}
      {...props}
    />
  );
};

export const CommentPin = ({ thread, hovered, onHover, onLeave, onClick }) => {
  const P = usePalette();
  const last = (thread.comments || [])[thread.comments.length - 1];
  const initial = (last && last.userInitial) || "?";
  const count = thread.comments?.length || 0;

  return (
    <Box
      position="absolute"
      left={`${thread.xPct}%`}
      top={`${thread.yPct}%`}
      transform="translate(-50%, -50%)"
      cursor="pointer"
      zIndex={20}
      data-html2canvas-ignore="true"
      onMouseEnter={(e) => onHover?.(e)}
      onMouseLeave={onLeave}
      onClick={(e) => onClick?.(e)}
    >
      <UserAvatar
        size="xs"
        icon={<Text fontSize="10px">{initial}</Text>}
        borderRadius="50% 50% 50% 6px"
        boxShadow={hovered ? "0 0 0 3px rgba(154,233,254,0.55)" : "0 0 0 2px #FFFFFF"}
        transition="box-shadow 0.15s ease"
      />
      {count > 1 && (
        <Badge
          position="absolute"
          bottom="-4px"
          right="-4px"
          borderRadius="full"
          fontSize="8px"
          minW="14px"
          h="14px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg="#121212"
          color={AVATAR_BG}
          border="1px solid #FFFFFF"
        >
          {count}
        </Badge>
      )}
    </Box>
  );
};

// Hover preview card. Positioned in viewport coordinates (supplied by the
// parent from the triggering mouse event) since it's portaled to the
// document root and percent-of-container isn't meaningful there.
export const CommentTooltip = ({ x, y, thread }) => {
  const P = usePalette();
  const last = (thread.comments || [])[thread.comments.length - 1];
  if (!last) return null;

  return (
    <Portal>
      <Flex
        position="fixed"
        left={`${x}px`}
        top={`${y}px`}
        transform="translate(-8px, -100%)"
        bg={P.surface}
        border={`1px solid ${P.border}`}
        borderRadius="full"
        boxShadow="0px 4px 14px rgba(0,0,0,0.08)"
        p="6px 16px 6px 6px"
        align="center"
        gap="8px"
        w="max-content"
        maxW="260px"
        zIndex={1600}
        pointerEvents="none"
      >
        <UserAvatar size="xs" icon={<Text fontSize="10px">{last.userInitial}</Text>} />
        <Text fontSize="14px" fontWeight="700" color={P.text} whiteSpace="nowrap">
          {last.userName}
        </Text>
        <Text fontSize="12px" color={P.textMuted} whiteSpace="nowrap">
          {formatRelativeTime(last.createdAt)}
        </Text>
      </Flex>
    </Portal>
  );
};

export const CommentComposer = ({ x, y, onSubmit, onCancel }) => {
  const P = usePalette();
  const [value, setValue] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!value.trim() || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit(value.trim());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Portal>
      <Flex
        position="fixed"
        left={`${x}px`}
        top={`${y}px`}
        align="center"
        gap="10px"
        w="300px"
        zIndex={1600}
      >
        <Flex
          align="center"
          justify="center"
          borderRadius="50% 50% 50% 6px"
          bg={AVATAR_BG}
          boxSize="32px"
          flexShrink={0}
        >
          <ChatBubbleOvalLeftIcon width={16} height={16} color={P.text} />
        </Flex>
        <Flex
          align="center"
          flex="1"
          bg={P.surface}
          border={`1px solid ${P.border}`}
          borderRadius="full"
          boxShadow="0px 4px 14px rgba(0,0,0,0.1)"
          pl="14px"
          pr="4px"
          py="4px"
          gap="6px"
        >
          <Input
            variant="unstyled"
            placeholder="Add a comment"
            fontSize="14px"
            color={P.text}
            value={value}
            autoFocus
            onChange={(e) => setValue(e.target.value)}
            onBlur={() => {
              if (!value.trim()) onCancel?.();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSubmit();
              }
              if (e.key === "Escape") onCancel?.();
            }}
          />
          <RoundArrowButton
            ariaLabel="Submit comment"
            isLoading={submitting}
            isDisabled={!value.trim()}
            onClick={handleSubmit}
          />
        </Flex>
      </Flex>
    </Portal>
  );
};

const CommentRow = ({ comment }) => {
  const P = usePalette();
  const toast = useToast();

  return (
    <Flex align="flex-start" gap="10px" mb="18px">
      <UserAvatar size="sm" icon={<Text fontSize="14px">{comment.userInitial}</Text>} />
      <Box flex="1">
        <Flex align="baseline" gap="8px">
          <Text fontSize="15px" fontWeight="700" color={P.text}>
            {comment.userName}
          </Text>
          <Text fontSize="12px" color={P.textMuted}>
            {formatRelativeTime(comment.createdAt)}
          </Text>
        </Flex>
        <Text fontSize="14px" color={P.text} whiteSpace="pre-wrap" mt="2px">
          {comment.text}
        </Text>
      </Box>
      <Menu placement="bottom-end">
        <MenuButton
          as={IconButton}
          aria-label="Comment actions"
          icon={<EllipsisHorizontalIcon width={18} height={18} color={P.textMuted} />}
          variant="ghost"
          size="xs"
        />
        <MenuList minW="140px">
          <MenuItem
            fontSize="13px"
            onClick={() => {
              navigator.clipboard?.writeText(comment.text);
              toast({ title: "Comment copied.", status: "success", duration: 1200 });
            }}
          >
            Copy text
          </MenuItem>
        </MenuList>
      </Menu>
    </Flex>
  );
};

export const CommentThreadPanel = ({ isOpen, onClose, thread, anchor, canReply, onReply }) => {
  const P = usePalette();
  const [replyValue, setReplyValue] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen || !thread) return null;

  const PANEL_W = 360;
  const PANEL_MAX_H = 480;
  const PAD = 12;
  const GAP = 14;

  const vw = window.innerWidth || 0;
  const vh = window.innerHeight || 0;

  let left = anchor?.x ?? vw / 2;
  let top = (anchor?.y ?? vh / 2) + GAP;

  left = Math.max(PAD, Math.min(vw - PANEL_W - PAD, left));
  if (top + PANEL_MAX_H + PAD > vh) {
    top = Math.max(PAD, (anchor?.y ?? vh / 2) - PANEL_MAX_H - GAP);
  }

  const handleReply = async () => {
    if (!replyValue.trim() || submitting) return;
    setSubmitting(true);
    try {
      await onReply(thread, replyValue.trim());
      setReplyValue("");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Portal>
      <Flex
        position="fixed"
        left={`${left}px`}
        top={`${top}px`}
        w={`${PANEL_W}px`}
        maxH={`${PANEL_MAX_H}px`}
        bg={P.surface}
        borderRadius="20px"
        boxShadow="0px 12px 32px rgba(0,0,0,0.16)"
        direction="column"
        p="20px"
        zIndex={1650}
      >
        <Flex justify="space-between" align="center">
          <Text fontSize="20px" fontWeight="600" color={P.text}>
            Comment
          </Text>
          <IconButton
            aria-label="Close"
            icon={<XMarkIcon width={20} height={20} />}
            size="sm"
            variant="ghost"
            onClick={onClose}
          />
        </Flex>

        <Divider my="14px" borderColor={P.border} />

        <Box flex="1" overflowY="auto" pr="4px" minH="0">
          {(thread.comments || []).map((c) => (
            <CommentRow key={c.id} comment={c} />
          ))}
        </Box>

        {canReply && (
          <Flex align="center" gap="10px" mt="6px">
            <UserAvatar size="sm" />
            <Flex
              align="center"
              flex="1"
              bg={P.surface}
              border={`1px solid ${P.border}`}
              borderRadius="full"
              pl="14px"
              pr="4px"
              py="4px"
              gap="6px"
            >
              <Input
                variant="unstyled"
                placeholder="Reply"
                fontSize="14px"
                color={P.text}
                value={replyValue}
                onChange={(e) => setReplyValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleReply();
                  }
                }}
              />
              <RoundArrowButton
                ariaLabel="Send reply"
                isLoading={submitting}
                isDisabled={!replyValue.trim()}
                onClick={handleReply}
              />
            </Flex>
          </Flex>
        )}
      </Flex>
    </Portal>
  );
};
